import React, { useMemo } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Box,
  Divider,
  useTheme,
  useMediaQuery,
  Button,
  Chip,
  Avatar,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate } from "react-router-dom";

// Icons
import DashboardIcon from "@mui/icons-material/Dashboard";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import PaymentsIcon from "@mui/icons-material/Payments";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";

import { adminMenu, agentMenu } from "./menuConfig";
import { useAuth } from "../../context/AuthContext";
import { filterVisibleMenu } from "../../utils/featureAccess";

const drawerWidth = 270;

const ICONS = {
  Dashboard: <DashboardIcon />,
  Analytics: <AnalyticsIcon />,
  ListAlt: <ListAltIcon />,
  AddCircle: <AddCircleIcon />,
  PeopleAltOutlined: <PeopleAltOutlinedIcon />,
  Settings: <SettingsIcon />,
  ReceiptLong: <ReceiptLongIcon />,
  PersonAddAlt1: <PersonAddAlt1Icon />,
  AdminPanelSettings: <AdminPanelSettingsIcon />,
  AccountBalanceWallet: <AccountBalanceWalletIcon />,
  AccountBalance: <AccountBalanceIcon />,
  AttachMoney: <AttachMoneyIcon />,
  MoneyOff: <MoneyOffIcon />,
  Payments: <PaymentsIcon />,
  PersonOutline: <PersonOutlineIcon />,
  RequestQuote: <RequestQuoteIcon />,
};

const Sidebar = ({ role, activePage, mobileOpen, onMobileClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const safeRole = useMemo(() => {
    return typeof role === "string" && role.trim()
      ? role.trim().toLowerCase()
      : "user";
  }, [role]);

  const roleLabel = useMemo(() => {
    if (safeRole === "admin") return "Administration";
    if (safeRole === "agent") return "Agent";
    return "Utilisateur";
  }, [safeRole]);

  const sections = useMemo(() => {
    if (safeRole === "admin") return filterVisibleMenu(adminMenu,user);
    if (safeRole === "agent") return filterVisibleMenu(agentMenu,user);
    return [];
  }, [safeRole,user]);

  const handleLogout = () => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("areaId");
      localStorage.removeItem("selected_area");
      window.location.href = "/login";
    } catch {
      window.location.href = "/login";
    }
  };

  const isSelected = (path) => {
    if (!activePage || !path) return false;
    return activePage === path || activePage.startsWith(path + "/");
  };

  const handleNavigate = (path) => {
    if (!path) return;
    navigate(path);
    if (isMobile) onMobileClose?.();
  };

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#f7f8fa",
      }}
    >
      <Toolbar />

      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 1.5,
          bgcolor: "#ffffff",
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <SidebarHeader roleLabel={roleLabel} />
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          px: 1.25,
          pt: 1.25,
          overflowY: "auto",
          minHeight: 0,
        }}
      >
        {sections.map((section) => (
          <Box key={section.section} sx={{ mb: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                px: 1.25,
                pb: 0.75,
                display: "block",
                color: "text.secondary",
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {section.section}
            </Typography>

            <List sx={{ p: 0 }}>
              {section.items.map((item) => {
                const selected = isSelected(item.path);
                const iconEl = ICONS[item.icon] || <ListAltIcon />;

                return (
                  <ListItemButton
                    key={item.path}
                    selected={selected}
                    onClick={() => handleNavigate(item.path)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      px: 1.25,
                      py: 1,
                      "&.Mui-selected": {
                        bgcolor: "rgba(25, 118, 210, 0.10)",
                        "&:hover": { bgcolor: "rgba(25, 118, 210, 0.14)" },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        color: selected ? "primary.main" : "text.secondary",
                      }}
                    >
                      {iconEl}
                    </ListItemIcon>

                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        sx: {
                          fontWeight: selected ? 800 : 600,
                          fontSize: 14,
                        },
                      }}
                    />
                  </ListItemButton>
                );
              })}
            </List>

            <Divider sx={{ mt: 1.25 }} />
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          p: 2,
          bgcolor: "#ffffff",
          borderTop: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          color="error"
          fullWidth
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{ borderRadius: 2, py: 1, fontWeight: 800 }}
        >
          Déconnexion
        </Button>

        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 1.25,
            textAlign: "center",
            color: "text.secondary",
          }}
        >
          DN-Force • v1
        </Typography>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={Boolean(mobileOpen)}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            borderRight: "1px solid",
            borderColor: "divider",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          borderRight: "1px solid",
          borderColor: "divider",
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

function SidebarHeader({ roleLabel }) {
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Avatar
          variant="rounded"
          sx={{
            width: 42,
            height: 42,
            bgcolor: "primary.main",
            fontWeight: 900,
          }}
        >
          D
        </Avatar>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 900, lineHeight: 1.1 }}
            noWrap
          >
            DN-Force
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontWeight: 700 }}
            noWrap
          >
            {roleLabel}
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Chip size="small" label="PRO" sx={{ fontWeight: 900 }} />
      </Box>

      <Typography
        variant="caption"
        sx={{ display: "block", mt: 1, color: "text.secondary" }}
      >
        Dieu est Notre Force
      </Typography>
    </Box>
  );
}

export default Sidebar;
